import React from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Chip,
  Container,
  FormControlLabel,
  Grid,
  Icon,
  IconButton,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@material-ui/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMapMarkerAlt } from '@fortawesome/free-solid-svg-icons';
import { Loader, TablePaginationWrapper, Title, DateWeekRangeSelector } from '../../elements';
import { Skeleton } from '../../elements/ui/skeleton';
import NoDataMessage from '../../components/shared/NoDataMessage';
import LocationPickerDialog from '../../components/LocationPickerDialog';
import { LoaderSize, DEFAULT_ROWS_PAGE_OPTIONS, DEFAULT_ROWS_PER_PAGE } from '../../constants/constants';
import { ApiService } from '../../hooks';
import useAuthContext from '../../hooks/useAuthContext';
import { extractApiErrorMessage, notifications } from '../../utils';
import {
  HealthSystemAPIControllersFeedSupplyResponsesFeedSiloResponse,
  HealthSystemDomainFeedSupplyQueriesFeedBatchResult
} from '../../api/HealthSystemApi';
import { LocationStable } from '../../models/locations';
import { useStyles } from '../LocationView/styles.ts';

const SiloView: React.FC = () => {
  const classes = useStyles();
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const apiService = new ApiService();
  const { state } = useAuthContext();

  const [silo, setSilo] = React.useState<HealthSystemAPIControllersFeedSupplyResponsesFeedSiloResponse | null>(null);
  const [loaded, setLoaded] = React.useState(false);
  const [isBusy, setIsBusy] = React.useState(true);
  const [isEditing, setIsEditing] = React.useState(false);

  // Editable fields
  const [siloNumber, setSiloNumber] = React.useState('');
  const [name, setName] = React.useState('');
  const [isActive, setIsActive] = React.useState(true);
  const [selectedLocationIds, setSelectedLocationIds] = React.useState<string[]>([]);

  // Location picker dialog
  const [pickerOpen, setPickerOpen] = React.useState(false);

  // Feed batches
  const [batches, setBatches] = React.useState<HealthSystemDomainFeedSupplyQueriesFeedBatchResult[]>([]);
  const [batchesLoaded, setBatchesLoaded] = React.useState(false);
  const [batchesBusy, setBatchesBusy] = React.useState(false);
  const [batchPage, setBatchPage] = React.useState(0);
  const [batchRowsPerPage, setBatchRowsPerPage] = React.useState(DEFAULT_ROWS_PER_PAGE);
  const [batchTotal, setBatchTotal] = React.useState(0);
  const [selectedDateFrom, setSelectedDateFrom] = React.useState<Date | null>(null);
  const [selectedDateTo, setSelectedDateTo] = React.useState<Date | null>(null);

  const resetFieldsToOriginal = (data: HealthSystemAPIControllersFeedSupplyResponsesFeedSiloResponse) => {
    setSiloNumber(String(data.siloNumber ?? ''));
    setName(data.name ?? '');
    setIsActive(data.isActive ?? true);
    setSelectedLocationIds(data.locations?.map((l) => l.locationId) ?? []);
  };

  const loadSilo = () => {
    if (!id || !state.farmId) return;
    setIsBusy(true);
    setLoaded(false);

    apiService.api.feedSilos
      .feedSilosGetByFarm({ farmId: state.farmId })
      .then((result) => {
        const found = result.data.find((s) => s.id === id);
        if (!found) {
          notifications.error(t('f-com-api-error'));
          return;
        }
        setSilo(found);
        resetFieldsToOriginal(found);
        setLoaded(true);
      })
      .catch((e) => {
        notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
      })
      .finally(() => {
        setIsBusy(false);
      });
  };

  const loadBatches = (page = batchPage, size = batchRowsPerPage) => {
    if (!id || !state.farmId) return;
    setBatchesBusy(true);

    apiService.api.feedSilos
      .feedSilosGetBatches({
        farmId: state.farmId,
        siloId: id,
        Page: page + 1,
        Size: size,
        ...(selectedDateFrom ? { StartDate: selectedDateFrom.toISOString() } : {}),
        ...(selectedDateTo ? { EndDate: selectedDateTo.toISOString() } : {})
      })
      .then((result) => {
        setBatches(result.data.items ?? []);
        setBatchTotal(result.data.totalCount ?? 0);
        setBatchesLoaded(true);
      })
      .catch((e) => {
        notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
      })
      .finally(() => {
        setBatchesBusy(false);
      });
  };

  React.useEffect(() => {
    loadSilo();
  }, [id]);

  React.useEffect(() => {
    if (!loaded) loadSilo();
  }, [loaded]);

  React.useEffect(() => {
    if (loaded) loadBatches(batchPage, batchRowsPerPage);
  }, [loaded, batchPage, batchRowsPerPage]);

  const handleEditClick = () => setIsEditing(true);

  const handleCancelClick = () => {
    if (silo) {
      resetFieldsToOriginal(silo);
    }
    setIsEditing(false);
  };

  const handleSaveClick = () => {
    const num = parseInt(siloNumber, 10);
    if (!siloNumber || isNaN(num) || num <= 0) {
      notifications.error(t('f-val-silo-number'));
      return;
    }
    if (!id || !state.farmId) {
      notifications.error(t('f-com-api-error'));
      return;
    }

    setIsBusy(true);
    apiService.api.feedSilos
      .feedSilosUpdate(
        { farmId: state.farmId, siloId: id },
        {
          siloNumber: num,
          name: name || null,
          isActive,
          locationIds: selectedLocationIds
        }
      )
      .then(() => {
        notifications.success(t('f-silo-updated'));
        setIsEditing(false);
        setLoaded(false);
      })
      .catch((e) => {
        notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
      })
      .finally(() => {
        setIsBusy(false);
      });
  };

  const handleBatchSearch = () => {
    setBatchPage(0);
    loadBatches(0, batchRowsPerPage);
  };

  const formatDate = (dateString: string) => new Date(dateString).toLocaleDateString();

  const renderLocationStableText = (stable: LocationStable) => {
    if (stable === LocationStable.Unknown) {
      return '-';
    }
    return LocationStable[stable];
  };

  const BATCH_SKELETON_COLS = 6;

  return (
    <Container maxWidth="lg">
      <Grid container spacing={2}>
        {/* Header */}
        <Loader isLoading={isBusy} size={LoaderSize.DEFAULT_SIZE} />

        {loaded && silo && (
          <>
            {/* Details card — inline edit */}
            <Grid item xs={12}>
              <Paper elevation={1} style={{ padding: 16 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                        <FontAwesomeIcon icon={faMapMarkerAlt} size="2x" className={classes.paperHeaderIcon} />
                        <Title>{t('f-silo-overview')}</Title>
                      </Box>
                      <Box display="flex" alignItems="center">
                        <IconButton color="primary" size="medium" onClick={() => setLoaded(false)}>
                          <Icon>refresh</Icon>
                        </IconButton>
                        {!isEditing ? (
                          <Button
                            variant="outlined"
                            color="primary"
                            startIcon={<Icon>edit</Icon>}
                            onClick={handleEditClick}
                            disabled={isBusy}
                          >
                            {t('f-com-edit')}
                          </Button>
                        ) : (
                          <Box>
                            <Button
                              variant="outlined"
                              onClick={handleCancelClick}
                              disabled={isBusy}
                              style={{ marginRight: 8 }}
                            >
                              {t('f-com-cancel')}
                            </Button>
                            <Button
                              variant="contained"
                              color="primary"
                              startIcon={<Icon>save</Icon>}
                              onClick={handleSaveClick}
                              disabled={isBusy}
                            >
                              {t('f-com-save')}
                            </Button>
                          </Box>
                        )}
                      </Box>
                    </Box>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="textSecondary">
                      {t('f-silo-number')}
                    </Typography>
                    {isEditing ? (
                      <TextField
                        fullWidth
                        type="number"
                        value={siloNumber}
                        onChange={(e) => setSiloNumber(e.target.value)}
                        required
                        disabled={isBusy}
                        inputProps={{ min: 1 }}
                      />
                    ) : (
                      <Typography variant="body1">
                        <strong>{silo.siloNumber}</strong>
                      </Typography>
                    )}
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="textSecondary">
                      {t('f-silo-name')}
                    </Typography>
                    {isEditing ? (
                      <TextField fullWidth value={name} onChange={(e) => setName(e.target.value)} disabled={isBusy} />
                    ) : (
                      <Typography variant="body1">
                        <strong>{silo.name || '-'}</strong>
                      </Typography>
                    )}
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="textSecondary">
                      {t('f-com-status')}
                    </Typography>
                    {isEditing ? (
                      <Box mt={1}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={isActive}
                              onChange={(e) => setIsActive(e.target.checked)}
                              color="primary"
                              disabled={isBusy}
                            />
                          }
                          label={t('f-com-active')}
                        />
                      </Box>
                    ) : (
                      <Typography variant="body1">
                        <strong>{silo.isActive ? t('f-com-active') : t('f-com-inactive')}</strong>
                      </Typography>
                    )}
                  </Grid>

                  {isEditing && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="textSecondary">
                        {t('f-silo-linked-locations')}
                      </Typography>
                      <Box mt={1}>
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<Icon>edit_location_alt</Icon>}
                          onClick={() => setPickerOpen(true)}
                          disabled={isBusy}
                        >
                          {t('f-silo-linked-locations')} ({selectedLocationIds.length})
                        </Button>
                      </Box>
                    </Grid>
                  )}
                </Grid>
              </Paper>
            </Grid>

            {/* Linked Locations table */}
            <Grid item xs={12}>
              <Paper elevation={1} style={{ padding: 16 }}>
                <Grid container direction="row" style={{ marginBottom: 8 }}>
                  <Grid container item spacing={1} xs={10} alignItems="center">
                    <Grid item>
                      <Icon color="primary" style={{ fontSize: 28 }}>
                        location_on
                      </Icon>
                    </Grid>
                    <Grid item>
                      <Title>{t('f-silo-linked-locations')}</Title>
                    </Grid>
                    <Grid item>
                      <Chip label={silo.locations?.length ?? 0} variant="outlined" />
                    </Grid>
                  </Grid>
                </Grid>
                {silo.locations && silo.locations.length > 0 ? (
                  <TableContainer>
                    <Table size="small">
                      <TableHead style={{ background: '#eceff1' }}>
                        <TableRow>
                          <TableCell>{t('f-chip-number')}</TableCell>
                          <TableCell>{t('f-loc-stable')}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {silo.locations.map((loc) => (
                          <TableRow key={loc.locationId} hover>
                            <TableCell>{loc.locationNumber || '-'}</TableCell>
                            <TableCell>{renderLocationStableText(loc.stableType as number)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <NoDataMessage text={t('f-no-data')} />
                )}
              </Paper>
            </Grid>

            {/* Feed Batches card */}
            <Grid item xs={12}>
              <Paper elevation={1} style={{ padding: 16 }}>
                <Grid container direction="row" style={{ marginBottom: 8 }}>
                  <Grid container item spacing={1} xs={10} alignItems="center">
                    <Grid item>
                      <Icon color="primary" style={{ fontSize: 28 }}>
                        local_shipping
                      </Icon>
                    </Grid>
                    <Grid item>
                      <Title>{t('f-silo-feed-batches')}</Title>
                    </Grid>
                    <Grid item>
                      <Chip label={batchTotal} variant="outlined" />
                    </Grid>
                  </Grid>
                </Grid>

                <DateWeekRangeSelector
                  onChange={(fromDate, toDate) => {
                    setSelectedDateFrom(fromDate);
                    setSelectedDateTo(toDate);
                  }}
                  fullWidth
                  allowFuture={false}
                />
                <Box mt={1}>
                  <Button
                    variant="contained"
                    color="default"
                    onClick={handleBatchSearch}
                    startIcon={<Icon>search</Icon>}
                  >
                    {t('f-search')}
                  </Button>
                </Box>

                <TablePaginationWrapper
                  rowsPerPageOptions={DEFAULT_ROWS_PAGE_OPTIONS}
                  count={batchTotal}
                  rowsPerPage={batchRowsPerPage}
                  page={batchPage}
                  onPageChange={(_e, p) => setBatchPage(p)}
                  onRowsPerPageChange={(e) => {
                    setBatchRowsPerPage(parseInt(e.target.value, 10));
                    setBatchPage(0);
                  }}
                >
                  <TableContainer>
                    <Table size="small">
                      <TableHead style={{ background: '#eceff1' }}>
                        <TableRow>
                          <TableCell>{t('f-silo-delivery-date')}</TableCell>
                          <TableCell>{t('f-silo-feed-type')}</TableCell>
                          <TableCell>{t('f-silo-quantity-kg')}</TableCell>
                          <TableCell>{t('f-silo-supplier')}</TableCell>
                          <TableCell>{t('f-silo-co2e-per-kg')}</TableCell>
                          <TableCell>{t('f-silo-transport-doc')}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {batchesBusy &&
                          Array.from({ length: 5 }).map((_, i) => (
                            <TableRow key={i}>
                              {Array.from({ length: BATCH_SKELETON_COLS }).map((_, j) => (
                                <TableCell key={j}>
                                  <Skeleton className="h-4 w-full" />
                                </TableCell>
                              ))}
                            </TableRow>
                          ))}

                        {!batchesBusy &&
                          batchesLoaded &&
                          batches.map((batch) => (
                            <TableRow key={batch.id} hover>
                              <TableCell>{formatDate(batch.deliveryDate)}</TableCell>
                              <TableCell>{batch.feedType}</TableCell>
                              <TableCell>{batch.quantityKg}</TableCell>
                              <TableCell>{batch.feedSupplierName || '-'}</TableCell>
                              <TableCell>{batch.co2ePerKg != null ? batch.co2ePerKg : '-'}</TableCell>
                              <TableCell>{batch.transportDocRef || '-'}</TableCell>
                            </TableRow>
                          ))}

                        {!batchesBusy && batchesLoaded && batches.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={BATCH_SKELETON_COLS}>
                              <NoDataMessage text={t('f-no-data')} />
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </TablePaginationWrapper>
              </Paper>
            </Grid>
          </>
        )}
      </Grid>

      <LocationPickerDialog
        open={pickerOpen}
        selectedIds={selectedLocationIds}
        onClose={() => setPickerOpen(false)}
        onConfirm={({ ids }) => {
          setSelectedLocationIds(ids);
          setPickerOpen(false);
        }}
      />
    </Container>
  );
};

export default SiloView;
